/**
 * Réglages d'accessibilité de la suite SEmply.
 *
 * Trois réglages, pensés pour les personnes malvoyantes et complémentaires
 * de la palette « Daltonien » (qui, elle, est un choix de palette comme un
 * autre — cf. palettes.js) :
 *
 *   - la TAILLE DU TEXTE : un facteur appliqué à la racine du document, donc
 *     à tout ce qui est exprimé en `rem`/`em` — c'est-à-dire l'essentiel de
 *     la charte. Il s'ajoute au zoom du navigateur, il ne le remplace pas :
 *     quelqu'un qui zoome déjà à 150 % et règle ici 130 % obtient les deux.
 *   - le CONTRASTE RENFORCÉ : bordures et textes secondaires ramenés à la
 *     couleur du texte principal, liens soulignés, contour de focus épaissi.
 *     Ce n'est PAS une palette : il s'applique par-dessus n'importe laquelle,
 *     sombre comprise.
 *   - la RÉDUCTION DES ANIMATIONS : transitions et animations coupées.
 *
 * Les deux derniers ont trois positions — `auto` (suivre le système, via
 * `prefers-contrast` et `prefers-reduced-motion`), `on`, `off`. Le défaut est
 * `auto` : quelqu'un qui a réglé son système a déjà exprimé sa préférence,
 * inutile de la lui faire redire. Le contrôleur RÉSOUT la position effective
 * et pose le résultat en attribut sur `<html>` ; les feuilles de style ne
 * regardent que ces attributs, jamais les media queries — une seule règle,
 * un seul endroit où elle est décidée.
 *
 * Même architecture que le contrôleur de thème (controller.js) :
 * `subscribe`/`getSnapshot` pour `useSyncExternalStore`, stockage local,
 * aucune dépendance. La préférence est locale au produit pour l'instant ;
 * la remonter au service d'auth est le même chantier que pour le thème.
 */

import { createRemoteWriter } from './remote.js';

export const A11Y_STORAGE_KEY = 'semply-a11y';
export const A11Y_STORAGE_VERSION = 1;

/** Facteurs de taille proposés. 150 % est le plus grand qui laisse la sidebar
    de SEmplyCompta (256 px) et la barre de SEmplyPrevi utilisables. */
export const TEXT_SCALES = [1, 1.15, 1.3, 1.5];
export const DEFAULT_TEXT_SCALE = 1;

/** Positions des réglages à trois états. */
export const TRISTATES = ['auto', 'on', 'off'];
export const TRISTATE_LABELS = { auto: 'Comme le système', on: 'Activé', off: 'Désactivé' };

export const CONTRAST_QUERY = '(prefers-contrast: more)';
export const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

export const DEFAULT_A11Y = Object.freeze({
  textScale: DEFAULT_TEXT_SCALE,
  contrast: 'auto',
  motion: 'auto',
});

function media(query) {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null;
  return window.matchMedia(query);
}

/** Le système demande-t-il ce réglage ? `false` quand la question n'a pas de sens. */
export function systemPrefers(query) {
  return media(query)?.matches === true;
}

/** S'abonne à une media query. Renvoie toujours une fonction de désabonnement. */
export function watchMedia(query, onChange) {
  const q = media(query);
  if (!q) return () => {};
  const handler = (event) => onChange(event.matches);
  if (typeof q.addEventListener === 'function') {
    q.addEventListener('change', handler);
    return () => q.removeEventListener('change', handler);
  }
  if (typeof q.addListener === 'function') {
    q.addListener(handler);
    return () => q.removeListener(handler);
  }
  return () => {};
}

/** Position effective d'un réglage à trois états. */
export function resolveTristate(value, systemWants) {
  if (value === 'on') return true;
  if (value === 'off') return false;
  return systemWants === true;
}

/** Ramène un état lu du stockage à quelque chose de sûr. */
export function normalizeA11y(stored) {
  const scale = Number(stored?.textScale);
  return {
    textScale: TEXT_SCALES.includes(scale) ? scale : DEFAULT_TEXT_SCALE,
    contrast: TRISTATES.includes(stored?.contrast) ? stored.contrast : 'auto',
    motion: TRISTATES.includes(stored?.motion) ? stored.motion : 'auto',
  };
}

export function readStoredA11y(storage, key = A11Y_STORAGE_KEY) {
  if (!storage) return null;
  try {
    const raw = storage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const state = parsed?.state ?? parsed;
    return state && typeof state === 'object' ? state : null;
  } catch {
    return null;
  }
}

export function writeStoredA11y(storage, state, key = A11Y_STORAGE_KEY) {
  if (!storage) return;
  try {
    storage.setItem(key, JSON.stringify({ state, version: A11Y_STORAGE_VERSION }));
  } catch {
    // Stockage verrouillé : le réglage vaut pour la session, pas au-delà.
  }
}

/**
 * Pose les réglages effectifs sur `<html>`.
 *
 * `--a11y-text-scale` est lu par base.css (`html { font-size }`). Les deux
 * attributs `data-a11y-*` ne sont posés que quand le réglage est ACTIF : les
 * sélecteurs CSS restent courts et un attribut absent vaut « rien à faire ».
 */
export function applyA11y({ textScale, contrast, motion }) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (textScale === 1) root.style.removeProperty('--a11y-text-scale');
  else root.style.setProperty('--a11y-text-scale', String(textScale));
  if (contrast) root.setAttribute('data-a11y-contrast', 'more');
  else root.removeAttribute('data-a11y-contrast');
  if (motion) root.setAttribute('data-a11y-motion', 'reduce');
  else root.removeAttribute('data-a11y-motion');
}

export function createAccessibilityController(options = {}) {
  const {
    storage = typeof window !== 'undefined' ? window.localStorage : null,
    storageKey = A11Y_STORAGE_KEY,
    /** Synchronisation avec le portail : `{ load, save }` (cf. remote.js). */
    remote = null,
  } = options;

  const listeners = new Set();
  const ecrivain = createRemoteWriter(remote);
  let state = normalizeA11y(readStoredA11y(storage, storageKey));
  let snapshot = buildSnapshot();

  function effective(s = state) {
    return {
      textScale: s.textScale,
      contrast: resolveTristate(s.contrast, systemPrefers(CONTRAST_QUERY)),
      motion: resolveTristate(s.motion, systemPrefers(REDUCED_MOTION_QUERY)),
    };
  }

  function buildSnapshot() {
    const eff = effective();
    return {
      textScale: state.textScale,
      contrast: state.contrast,
      motion: state.motion,
      effectiveContrast: eff.contrast,
      effectiveMotion: eff.motion,
      systemContrast: systemPrefers(CONTRAST_QUERY),
      systemMotion: systemPrefers(REDUCED_MOTION_QUERY),
    };
  }

  function apply() {
    applyA11y(effective());
    snapshot = buildSnapshot();
    listeners.forEach((fn) => fn());
  }

  function commit(next, { remoteSave = true } = {}) {
    state = normalizeA11y({ ...state, ...next });
    writeStoredA11y(storage, state, storageKey);
    // Ce qui revient du portail (`sync`) n'y est pas renvoyé.
    if (remoteSave) ecrivain.schedule({ ...state });
    apply();
  }

  return {
    getSnapshot: () => snapshot,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    apply,
    setTextScale(scale) {
      if (!TEXT_SCALES.includes(Number(scale))) return;
      commit({ textScale: Number(scale) });
    },
    setContrast(value) {
      if (!TRISTATES.includes(value)) return;
      commit({ contrast: value });
    },
    setMotion(value) {
      if (!TRISTATES.includes(value)) return;
      commit({ motion: value });
    },
    /** Remet tout au défaut (suivre le système, texte à 100 %). */
    reset() {
      commit({ ...DEFAULT_A11Y });
    },
    /** Suit `prefers-contrast` et `prefers-reduced-motion` sans rechargement. */
    watchSystem() {
      const stops = [
        watchMedia(CONTRAST_QUERY, () => apply()),
        watchMedia(REDUCED_MOTION_QUERY, () => apply()),
      ];
      return () => stops.forEach((stop) => stop());
    },
    start() {
      apply();
      return this.watchSystem();
    },
    /**
     * Réaligne sur la préférence du compte lue au portail — mêmes règles que
     * le contrôleur de thème : le portail l'emporte, un portail muet ne
     * change rien. Renvoie `true` si quelque chose a été appliqué.
     */
    async sync() {
      if (!remote?.load) return false;
      let distant = null;
      try {
        distant = await remote.load();
      } catch {
        return false;
      }
      if (!distant || typeof distant !== 'object') return false;
      commit(distant, { remoteSave: false });
      return true;
    },
    flush: () => ecrivain.flush(),
  };
}

/* ── Annonces aux lecteurs d'écran ─────────────────────────────────────── */
/*
 * Une région `aria-live` unique, créée à la demande et masquée visuellement.
 * À appeler après une action dont le résultat n'est pas au focus : « Dossier
 * enregistré », « 12 écritures trouvées ». Le texte est vidé puis réécrit
 * pour que deux annonces identiques d'affilée soient toutes deux lues.
 */
const LIVE_ID = 'semply-a11y-live';

function liveRegion(politeness) {
  if (typeof document === 'undefined') return null;
  const id = `${LIVE_ID}-${politeness}`;
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('div');
    el.id = id;
    el.className = 'sr-only';
    el.setAttribute('aria-live', politeness);
    el.setAttribute('aria-atomic', 'true');
    document.body.appendChild(el);
  }
  return el;
}

export function announce(message, { assertive = false } = {}) {
  const el = liveRegion(assertive ? 'assertive' : 'polite');
  if (!el) return;
  el.textContent = '';
  // Le vidage et la réécriture dans la même tâche seraient fusionnés par le
  // navigateur : un tour de boucle entre les deux.
  setTimeout(() => {
    el.textContent = message;
  }, 30);
}
